import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { HEADERMENU_PATH } from '../../../routes/RoutesURL';
import { RootState } from '../../../store/rootReducer';
import { KYCAIResubmission } from '../../common/Header/Header';
import { isNonAuthenticatedUrl } from '../../login/utils';
import { InvestorStatus, UserStatus } from '../../login/utils/Status';
import { isUserProfileComplete } from '../../utils/validation';
import AccreditedInvestor from './components/accreditedInvestor';
import AccountDropdown from './components/common/AccountDropdown';
import OnboardingBack from './components/common/Back';
import OnboardingRightContainer from './components/common/Container';
import OnboardingMainContainer from './components/common/MainContainer';
import TypeOfInvestments from './components/investmentPreferences';
import NewKYCFlow from './components/kyc';
import SinpassForm from './components/kyc/Signpass';
import NDA from './components/nda/index';
import Accreditation from './components/profile/Accreditation';
import InvestorProfile from './components/profile/InvestorProfile';
import UserProfile from './components/profile/UserProfile';
import OnboardingSidebar from './components/sidebar';

export enum ONBOARDING_PATH {
    BASIC_PROFILE = 'basic-profile',
    ACCREDITATION = 'accreditation',
    INVESTOR_PROFILE = 'profile',
    NDA = 'nda',
    INVESTMENT_PREFERENCES = 'investment-preferences',
    KYC = 'kyc',
    SIGNPASS = 'singpass',
    ACCREDITED_INVESTOR = 'accredited-investor',
}

const OnboardingFlow: React.FC = () => {
    const [preferencesIndex, setPreferencesIndex] = useState<number>(1);
    return (
        <OnboardingMainContainer
            id="onboarding"
            leftSide={
                <OnboardingSidebar preferencesIndex={preferencesIndex} setPreferencesIndex={setPreferencesIndex} />
            }
            rightSide={
                <OnboardingRightSideComponent
                    preferencesIndex={preferencesIndex}
                    setPreferencesIndex={setPreferencesIndex}
                />
            }
        />
    );
};

export default OnboardingFlow;

type Props = {
    preferencesIndex: number;
    setPreferencesIndex: (f: number) => void;
};

const OnboardingRightSideComponent: React.FC<Props> = ({ preferencesIndex, setPreferencesIndex }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const investor = useSelector((state: RootState) => state.user.investor);

    const user = useSelector((state: RootState) => state.user.user);

    const [currentPath, setCurrentPath] = useState('');

    /**Get current Path */
    useEffect(() => {
        setCurrentPath(location.pathname);
    }, [location]);

    //If account not activate with otp
    useEffect(() => {
        if (
            !isNonAuthenticatedUrl(currentPath) &&
            isUserProfileComplete(user?.userProfile) &&
            UserStatus.pendingActivation === user?.status &&
            currentPath !== HEADERMENU_PATH.accountActivationWithOtp
        ) {
            navigate(HEADERMENU_PATH.accountActivationWithOtp);
        }
    }, [user, currentPath, navigate]);

    return (
        <>
            <div className="py-4 pt-0 hidden md:flex justify-between items-center">
                <OnboardingBack preferencesIndex={preferencesIndex} setPreferencesIndex={setPreferencesIndex} />
                <AccountDropdown />
            </div>
            <div>
                <OnboardingRightContainer>
                    {investor?.status?.kyc === InvestorStatus.PendingKYCResubmission &&
                    currentPath.includes(ONBOARDING_PATH.KYC) ? (
                        <KYCAIResubmission type="KYC" />
                    ) : null}
                    {investor?.status?.accreditation === InvestorStatus.PendingAIResubmission &&
                    currentPath.includes(ONBOARDING_PATH.ACCREDITED_INVESTOR) ? (
                        <KYCAIResubmission type="Accreditation" />
                    ) : null}
                </OnboardingRightContainer>
                <Routes>
                    {/** Profile */}
                    <Route path={ONBOARDING_PATH.BASIC_PROFILE} element={<UserProfile />} />
                    <Route path={ONBOARDING_PATH.ACCREDITATION} element={<Accreditation />} />
                    <Route path={ONBOARDING_PATH.INVESTOR_PROFILE} element={<InvestorProfile />} />
                    {/** End Profile */}

                    {/* NDA */}
                    <Route path={ONBOARDING_PATH.NDA} element={<NDA />} />
                    {/* NDA End */}

                    {/* Investment Preferences */}
                    <Route
                        path={ONBOARDING_PATH.INVESTMENT_PREFERENCES}
                        element={
                            <TypeOfInvestments
                                preferencesIndex={preferencesIndex}
                                setPreferencesIndex={setPreferencesIndex}
                            />
                        }
                    />

                    {/** KYC */}
                    <Route path={ONBOARDING_PATH.ACCREDITED_INVESTOR} element={<AccreditedInvestor />} />
                    <Route path={ONBOARDING_PATH.KYC} element={<NewKYCFlow />} />
                    <Route path={ONBOARDING_PATH.SIGNPASS} element={<SinpassForm />} />
                    {/** End KYC */}
                </Routes>
            </div>
        </>
    );
};
